/**
 * Blog Redesign JavaScript
 * Handles product filter scroll, tabs, and other interactions
 */

(function() {
  'use strict';

  /**
   * Product Filter Scroll Enhancement
   */
  class ProductFilterScroll {
    constructor() {
      this.scrollContainers = document.querySelectorAll('.blog-product-filter__scroll');
      if (!this.scrollContainers.length) return;

      this.init();
    }

    init() {
      this.scrollContainers.forEach(container => {
        // Add scroll shadows on hover/focus
        const productsContainer = container.querySelector('.blog-product-filter__products');
        if (!productsContainer) return;

        // Mouse drag scroll
        let isDown = false;
        let startX;
        let scrollLeft;

        productsContainer.addEventListener('mousedown', (e) => {
          isDown = true;
          productsContainer.style.cursor = 'grabbing';
          startX = e.pageX - productsContainer.offsetLeft;
          scrollLeft = container.scrollLeft;
        });

        productsContainer.addEventListener('mouseleave', () => {
          isDown = false;
          productsContainer.style.cursor = 'grab';
        });

        productsContainer.addEventListener('mouseup', () => {
          isDown = false;
          productsContainer.style.cursor = 'grab';
        });

        productsContainer.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          e.preventDefault();
          const x = e.pageX - productsContainer.offsetLeft;
          const walk = (x - startX) * 1.5;
          container.scrollLeft = scrollLeft - walk;
        });

        // Set initial cursor
        productsContainer.style.cursor = 'grab';
      });
    }
  }

  /**
   * Accordion Details Enhancement
   * Provides smooth open/close animation
   */
  class AccordionDetails {
    constructor() {
      this.accordions = document.querySelectorAll('.blog-product-filter');
      if (!this.accordions.length) return;

      this.init();
    }

    init() {
      this.accordions.forEach(accordion => {
        const summary = accordion.querySelector('summary');
        const content = accordion.querySelector('.blog-product-filter__content');

        if (!summary || !content) return;

        summary.addEventListener('click', (e) => {
          e.preventDefault();

          if (accordion.open) {
            // Close
            content.style.maxHeight = content.scrollHeight + 'px';
            requestAnimationFrame(() => {
              content.style.maxHeight = '0';
              content.style.overflow = 'hidden';
            });

            content.addEventListener('transitionend', function handler() {
              accordion.open = false;
              content.style.maxHeight = '';
              content.style.overflow = '';
              content.removeEventListener('transitionend', handler);
            });
          } else {
            // Open
            accordion.open = true;
            const height = content.scrollHeight;
            content.style.maxHeight = '0';
            content.style.overflow = 'hidden';

            requestAnimationFrame(() => {
              content.style.maxHeight = height + 'px';
            });

            content.addEventListener('transitionend', function handler() {
              content.style.maxHeight = '';
              content.style.overflow = '';
              content.removeEventListener('transitionend', handler);
            });
          }
        });

        // Add transition style
        content.style.transition = 'max-height 0.3s ease-out';
      });
    }
  }

  /**
   * Landing Page Filter Controller
   * Handles client-side filtering of articles by type and product on the landing page
   */
  class LandingPageFilter {
    constructor() {
      this.articles = document.querySelectorAll('[data-article-type]');
      this.typeTabs = document.querySelectorAll('[data-filter-type]');
      this.productBtns = document.querySelectorAll('[data-filter-product]');

      // Only initialize if we have filter elements (landing page)
      if (!this.typeTabs.length && !this.productBtns.length) return;
      if (!this.articles.length) return;

      this.activeType = 'all';
      this.activeProduct = null;
      this.emptyState = document.querySelector('.blog-filter-empty');

      // Visual indicator elements
      this.activeCount = document.getElementById('LandingFilterCount');
      this.clearBtn = document.getElementById('LandingFilterClear');

      // Sidebar links for URL persistence
      this.categoryLinks = document.querySelectorAll('[data-category-link]');
      this.homeLink = document.querySelector('.blog-sidebar__link[href^="/pages/blog"]');

      // Containers for articles/manuals toggle
      this.articlesContainer = document.getElementById('BlogArticlesContainer');
      this.manualsContainer = document.getElementById('BlogManualsContainer');
      this.productFilter = document.querySelector('.blog-product-filter');

      this.init();
    }

    init() {
      // Check URL for initial filter state (enables deep linking from Help menu)
      const urlParams = new URLSearchParams(window.location.search);
      const filterParam = urlParams.get('filter');

      if (filterParam) {
        this.activeType = filterParam;
        this.updateTabs();
        this.toggleView();
        this.filterArticles();
        this.updateFilterUI();
        this.updateSidebarLinks();
      }

      // Tab click handlers
      this.typeTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          this.activeType = tab.dataset.filterType;
          this.updateTabs();
          this.toggleView();
          this.filterArticles();
          this.updateFilterUI();
          this.updateSidebarLinks();
        });
      });

      // Product click handlers
      this.productBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const product = btn.dataset.filterProduct;
          // Toggle: click same product again to clear
          this.activeProduct = this.activeProduct === product ? null : product;
          this.updateProducts();
          this.filterArticles();
          this.updateFilterUI();
          this.updateSidebarLinks();
        });
      });

      // Clear button handler
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.activeProduct = null;
          this.updateProducts();
          this.filterArticles();
          this.updateFilterUI();
          this.updateSidebarLinks();
        });
      }

      // Initialize tab states on load
      this.updateTabs();
    }

    /**
     * Toggle between articles view and manuals view
     */
    toggleView() {
      const showManuals = this.activeType === 'manuals';

      if (this.articlesContainer) {
        this.articlesContainer.classList.toggle('is-hidden', showManuals);
      }
      if (this.manualsContainer) {
        this.manualsContainer.classList.toggle('is-hidden', !showManuals);
      }
      // Hide product filter when showing manuals
      if (this.productFilter) {
        this.productFilter.classList.toggle('is-hidden', showManuals);
      }
    }

    filterArticles() {
      // Skip filtering if showing manuals view
      if (this.activeType === 'manuals') return;

      let visibleCount = 0;

      this.articles.forEach(article => {
        const types = article.dataset.articleType || '';
        const products = article.dataset.articleProducts || '';

        // Match type: 'all' shows everything, specific type checks if article's types include it
        // Types can be comma-separated if article has multiple type tags
        const matchesType = this.activeType === 'all' || types.includes(this.activeType);
        const matchesProduct = !this.activeProduct || products.includes(this.activeProduct);

        const shouldShow = matchesType && matchesProduct;
        article.classList.toggle('is-hidden', !shouldShow);

        if (shouldShow) visibleCount++;
      });

      this.checkEmptyState(visibleCount);

      // Emit event for LoadMoreController to update counts
      document.dispatchEvent(new CustomEvent('blog:filters-updated'));
    }

    updateTabs() {
      this.typeTabs.forEach(tab => {
        const isActive = tab.dataset.filterType === this.activeType;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    updateProducts() {
      this.productBtns.forEach(btn => {
        const isActive = btn.dataset.filterProduct === this.activeProduct;
        btn.classList.toggle('is-active', isActive);
      });
    }

    checkEmptyState(visibleCount) {
      if (this.emptyState) {
        // Don't show empty state when in manuals view
        const showEmpty = visibleCount === 0 && this.activeType !== 'manuals';
        this.emptyState.classList.toggle('is-visible', showEmpty);
      }
    }

    /**
     * Update visual indicators (count badge and clear button)
     */
    updateFilterUI() {
      const hasProductFilter = this.activeProduct !== null;

      if (this.activeCount) {
        this.activeCount.style.display = hasProductFilter ? 'inline' : 'none';
      }
      if (this.clearBtn) {
        this.clearBtn.style.display = hasProductFilter ? 'inline-flex' : 'none';
      }
    }

    /**
     * Update sidebar category links to include active filters
     * This enables URL-based filter persistence when navigating to categories
     */
    updateSidebarLinks() {
      // Update category blog links
      this.categoryLinks.forEach(link => {
        const handle = link.dataset.categoryLink;
        if (!handle) return;

        let url = `/blogs/${handle}`;

        // Build tag combination
        const tags = [];
        if (this.activeType && this.activeType !== 'all' && this.activeType !== 'manuals') {
          tags.push(this.activeType);
        }
        if (this.activeProduct) {
          tags.push(this.activeProduct);
        }

        if (tags.length > 0) {
          url += `/tagged/${tags.join('+')}`;
        }

        link.href = url;
      });

      // Update Home link with filter query param
      if (this.homeLink) {
        let homeUrl = '/pages/blog';
        if (this.activeType && this.activeType !== 'all') {
          homeUrl += `?filter=${this.activeType}`;
        }
        this.homeLink.href = homeUrl;
      }
    }
  }

  /**
   * Manuals Tab Controller
   * Handles the Manuals/Apps tab toggle on category blog pages
   */
  class ManualsTabController {
    constructor() {
      this.manualsTab = document.querySelector('[data-filter-type="manuals"]');
      // Only initialize on category pages (where there's no LandingPageFilter in control)
      // LandingPageFilter handles landing page tabs
      if (!this.manualsTab) return;

      // Check if we're on a category page (has articles container but no article data attributes for JS filtering)
      this.articlesContainer = document.getElementById('BlogArticlesContainer');
      this.manualsContainer = document.getElementById('BlogManualsContainer');
      this.productFilter = document.querySelector('.blog-product-filter');

      if (!this.articlesContainer || !this.manualsContainer) return;

      // Only initialize if LandingPageFilter is not active (no data-article-type elements)
      const hasLandingFilter = document.querySelectorAll('[data-article-type]').length > 0;
      if (hasLandingFilter) return;

      this.allTabs = document.querySelectorAll('.blog-tabs__link');
      this.manualsTab = document.querySelector('[data-filter-type="manuals"]');
      this.isShowingManuals = false;

      this.init();
    }

    init() {
      // Check URL for initial filter state (enables deep linking from Help menu)
      const urlParams = new URLSearchParams(window.location.search);
      const filterParam = urlParams.get('filter');

      if (filterParam === 'manuals') {
        this.showManuals();
        this.updateTabs();
      }

      // Only add click handler to the manuals tab button
      if (this.manualsTab) {
        this.manualsTab.addEventListener('click', (e) => {
          e.preventDefault();
          this.showManuals();
          this.updateTabs();
        });
      }

      // Add click handlers to other tabs to restore articles view if showing manuals
      this.allTabs.forEach(tab => {
        if (tab !== this.manualsTab) {
          tab.addEventListener('click', () => {
            if (this.isShowingManuals) {
              this.showArticles();
            }
          });
        }
      });
    }

    showManuals() {
      this.isShowingManuals = true;
      this.articlesContainer.classList.add('is-hidden');
      this.manualsContainer.classList.remove('is-hidden');
      if (this.productFilter) {
        this.productFilter.classList.add('is-hidden');
      }
    }

    showArticles() {
      this.isShowingManuals = false;
      this.articlesContainer.classList.remove('is-hidden');
      this.manualsContainer.classList.add('is-hidden');
      if (this.productFilter) {
        this.productFilter.classList.remove('is-hidden');
      }
    }

    updateTabs() {
      // Remove active state from ALL tabs (including <a> links)
      this.allTabs.forEach(tab => {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
      });
      // Add active state only to manuals tab
      if (this.manualsTab) {
        this.manualsTab.classList.add('is-active');
        this.manualsTab.setAttribute('aria-selected', 'true');
      }
    }
  }

  /**
   * Load More Controller
   * Handles revealing additional pre-rendered articles on the landing page
   */
  class LoadMoreController {
    constructor() {
      this.button = document.querySelector('[data-load-more]');
      if (!this.button) return;

      this.grid = document.getElementById('BlogArticleGrid');
      this.container = document.getElementById('BlogLoadMore');
      this.visibleCountEl = this.button.querySelector('[data-visible-count]');
      this.totalCountEl = this.button.querySelector('[data-total-count]');

      this.perPage = parseInt(this.button.dataset.perPage) || 12;
      this.totalArticles = parseInt(this.button.dataset.totalArticles) || 0;
      this.currentBatch = parseInt(this.button.dataset.currentBatch) || 0;
      this.maxBatch = Math.ceil(this.totalArticles / this.perPage) - 1;

      // Track all article wrappers
      this.articleWrappers = this.grid ? this.grid.querySelectorAll('.blog-article-wrapper') : [];

      this.init();
    }

    init() {
      this.button.addEventListener('click', () => this.loadMore());

      // Listen for filter changes to update counts
      document.addEventListener('blog:filters-updated', () => this.updateCounts());
    }

    loadMore() {
      // Move to next batch
      this.currentBatch++;
      this.button.dataset.currentBatch = this.currentBatch;

      // Reveal articles in the next batch
      this.articleWrappers.forEach(wrapper => {
        const batch = parseInt(wrapper.dataset.batch);
        if (batch <= this.currentBatch) {
          wrapper.classList.remove('is-paginated');
        }
      });

      // Update visible count
      this.updateCounts();

      // Hide button if we've shown all batches
      if (this.currentBatch >= this.maxBatch) {
        this.container.classList.add('is-hidden');
      }

      // Smooth scroll to newly revealed content (optional)
      // Disabled by default - uncomment if desired
      // this.scrollToNewContent();
    }

    updateCounts() {
      // Count currently visible (not paginated AND not filtered out) articles
      let visibleCount = 0;
      this.articleWrappers.forEach(wrapper => {
        const isPaginated = wrapper.classList.contains('is-paginated');
        const card = wrapper.querySelector('.article-card-redesign');
        const isFiltered = card && card.classList.contains('is-hidden');

        if (!isPaginated && !isFiltered) {
          visibleCount++;
        }
      });

      // Update the count display
      if (this.visibleCountEl) {
        // Show the lesser of visible count or total available
        const displayCount = Math.min(
          (this.currentBatch + 1) * this.perPage,
          this.totalArticles
        );
        this.visibleCountEl.textContent = displayCount;
      }
    }

    scrollToNewContent() {
      const lastVisibleWrapper = Array.from(this.articleWrappers)
        .filter(w => !w.classList.contains('is-paginated'))
        .slice(-this.perPage)[0];

      if (lastVisibleWrapper) {
        lastVisibleWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  /**
   * No Articles Toast Handler
   * Shows a toast notification when clicking products without articles
   */
  class NoArticlesToast {
    constructor() {
      this.buttons = document.querySelectorAll('[data-no-articles]');
      if (!this.buttons.length) return;

      this.toast = null;
      this.hideTimeout = null;
      this.init();
    }

    init() {
      this.buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const productName = btn.dataset.productName || 'this product';
          this.show(`No articles yet for ${productName}`);
        });
      });
    }

    show(message) {
      // Clear any existing timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
      }

      // Create toast if it doesn't exist
      if (!this.toast) {
        this.toast = document.createElement('div');
        this.toast.className = 'blog-toast';
        this.toast.setAttribute('role', 'status');
        this.toast.setAttribute('aria-live', 'polite');
        document.body.appendChild(this.toast);
      }

      // Update message and show
      this.toast.textContent = message;

      // Force reflow to restart animation
      this.toast.classList.remove('is-visible');
      void this.toast.offsetWidth;
      this.toast.classList.add('is-visible');

      // Auto-hide after 3 seconds
      this.hideTimeout = setTimeout(() => {
        this.toast.classList.remove('is-visible');
      }, 3000);
    }
  }

  /**
   * Initialize all components on DOM ready
   */
  function init() {
    new ProductFilterScroll();
    new AccordionDetails();
    new NoArticlesToast();
    new LandingPageFilter();
    new ManualsTabController();
    new LoadMoreController();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.classList.contains('section-blog-redesign')) {
      init();
    }
  });
})();
